import { useState } from "react"
import styled from "styled-components"

import { IconButton, Popover, Text } from "@minuthq/meatball-ui-react"
import { useTimeout } from "usehooks-ts"

import { useFetchApiClientSecret } from "src/data/apiClients/apiClientQueries"
import { IApiClient } from "src/data/apiClients/apiClientTypes"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { CopyBox } from "src/ui/CopyText/CopyBox"
import { GridTable } from "src/ui/GridTable/GridTable"
import { MoreButton } from "src/ui/GridTable/MoreButton"
import {
  TableColumn,
  useTableColumns,
} from "src/ui/GridTable/useTableColumns/useTableColumns"
import CopyIcon from "src/ui/icons/copy.svg"
import ExpandDownIcon from "src/ui/icons/expand-down.svg"
import { InternalLink } from "src/ui/Link/InternalLink"
import { MCard } from "src/ui/MCard/MCard"
import { MSkeleton } from "src/ui/MSkeleton/MSkeleton"
import { MTooltip } from "src/ui/MTooltip/MTooltip"
import { spacing } from "src/ui/spacing"

export function ApiClientsTable({
  clients,
  onEdit,
  onDelete,
}: {
  clients: IApiClient[]
  onEdit: (client: IApiClient) => void
  onDelete: (client: IApiClient) => void
}) {
  const { t, langKeys } = useTranslate()

  const [selectedSecret, setSelectedSecret] = useState<{
    anchor: HTMLElement
    clientId: IApiClient["client_id"]
  }>()

  const [openClientIdCopyTooltip, setOpenTooltip] = useState<{
    timeout: number
    clientId: IApiClient["client_id"]
  }>()

  useTimeout(() => {
    setOpenTooltip(undefined)
  }, openClientIdCopyTooltip?.timeout ?? null)

  function handleRowClick(index: number) {
    onEdit(getClientByIndex(index))
  }

  function getClientByIndex(index: number): IApiClient {
    const client = clients[index]

    if (!client) {
      throw Error("Client was undefined")
    }

    return client
  }

  async function handleCopyClientIdClick(clientId: IApiClient["client_id"]) {
    await navigator.clipboard.writeText(clientId)
    setOpenTooltip({ timeout: 2000, clientId })
  }

  const tableColumns: TableColumn<IApiClient>[] = [
    {
      value: "name",
      label: t(langKeys.api_clients_client),
      renderCell: (client) => (
        <InternalLink to={Routes.ApiClientsEdit.location(client.client_id)}>
          {client.name}
        </InternalLink>
      ),
    },
    {
      value: "client_id",
      label: t(langKeys.api_clients_client_id),
      disableClickPropagation: true,
      renderCell: (client) => (
        <ClientIdBox>
          <Text variant="body">{client.client_id}</Text>

          <MTooltip
            title={"Copied!"}
            open={openClientIdCopyTooltip?.clientId === client.client_id}
          >
            <IconButton
              Icon={CopyIcon}
              variant="minimal"
              size="small"
              onClick={() => {
                handleCopyClientIdClick(client.client_id)
              }}
            />
          </MTooltip>
        </ClientIdBox>
      ),
    },
    {
      value: "client_secret",
      label: t(langKeys.api_clients_client_secret),
      disableClickPropagation: true,
      renderCell: (client) => (
        <div>
          <TextBox>
            <Text variant="body">••••••••••••••</Text>

            <IconButton
              Icon={ExpandDownIcon}
              variant="minimal"
              size="small"
              onClick={(event) => {
                setSelectedSecret({
                  anchor: event.currentTarget,
                  clientId: client.client_id,
                })
              }}
            />
          </TextBox>
        </div>
      ),
    },
    {
      value: "authorization_code_grant.redirect_uris",
      label: t(langKeys.api_clients_redirect_uri),
      disableClickPropagation: true,
      renderCell: (client) => (
        <TextBox>
          <Text variant="body">
            {client.authorization_code_grant.redirect_uris[0]}
          </Text>

          {client.authorization_code_grant.redirect_uris.length > 1 && (
            <Popover
              trigger={
                <IconButton
                  Icon={ExpandDownIcon}
                  variant="minimal"
                  size="small"
                />
              }
              offset={8}
            >
              <MCard>
                <RedirectsList>
                  {client.authorization_code_grant.redirect_uris.map(
                    (redirectUri, index) => (
                      <li key={`${redirectUri}-${index}`}>
                        <Text variant="bodyS" color="secondary">
                          {redirectUri}
                        </Text>
                      </li>
                    )
                  )}
                </RedirectsList>
              </MCard>
            </Popover>
          )}
        </TextBox>
      ),
    },
    {
      value: "misc",
      label: "",
      renderCell: (client) => (
        <div>
          <MoreButton
            onDelete={() => onDelete(client)}
            onEdit={() => onEdit(client)}
          />
        </div>
      ),
    },
  ]

  const { headerElements, rows, templateColumns } = useTableColumns({
    columns: tableColumns,
    data: clients,
  })

  return (
    <div>
      <GridTable
        header={headerElements}
        rows={rows}
        templateColumns={templateColumns}
        useFallbackResponsiveMode
        onRowClick={handleRowClick}
      />

      {selectedSecret && (
        <Popover
          anchorElement={selectedSecret.anchor}
          open={true}
          onClose={() => setSelectedSecret(undefined)}
          offset={8}
        >
          <MCard padding={0}>
            <CopyClientSecret clientId={selectedSecret.clientId} />
          </MCard>
        </Popover>
      )}
    </div>
  )
}

export function CopyClientSecret({ clientId }: { clientId: string }) {
  const fetchApiClientSecret = useFetchApiClientSecret({ clientId })

  const clientSecret = fetchApiClientSecret.data?.client_secret

  if (!clientSecret) {
    return <MSkeleton variant="rect" height={48} width="30ch" />
  }

  return <CopyBox text={clientSecret} />
}

const TextBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.S};
`

const RedirectsList = styled.ul`
  padding: 0;
  margin: 0;
  list-style-type: none;
`

const ClientIdBox = styled.div`
  flex-direction: row;
  align-items: center;
  gap: ${spacing.S};
  justify-content: flex-start;
  white-space: nowrap;
  display: flex;
`
